import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger';
import { Exclude, Expose, Type } from 'class-transformer';
import { CommonEntityDto } from '../../common/dto/common-entity.dto';
import {
  IsBoolean,
  IsEnum,
  IsISO8601,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsPositive,
  IsString,
  IsUUID,
  Matches,
  Min,
  ValidateNested,
} from 'class-validator';
import { JobPhaseBumpApprovalStatus } from '../enums/job-phase-bump-approval-status.enum';
import { JobPhaseBumpCommonInterface } from '../interfaces/job-phase-bump-common.interface';
import { GeoMatchStatusEnum } from '../../common/enums/geo-match-status.enum';
import { GeographyPointDto } from '../../common/dto/geography-point.dto';

@Exclude()
export class JobPhaseBumpCommonDto
  extends CommonEntityDto
  implements JobPhaseBumpCommonInterface
{
  @ApiProperty({
    title: 'currentCost',
    description: 'CurrentCost of this payment',
  })
  @IsNumber()
  @Expose()
  currentCost!: number | null;

  @ApiProperty({
    title: 'Check',
    nullable: true,
  })
  @Expose()
  check!: string;

  @ApiProperty({
    title: 'Logged Hours',
    type: 'number',
  })
  @IsNumber()
  @Expose()
  loggedHours!: number;

  @ApiProperty({
    title: 'Approved Hours',
    type: 'number',
  })
  @IsNumber()
  @IsPositive()
  @Expose()
  approvedHours!: number;

  @ApiProperty({
    title: 'Estimated Hours',
    type: 'number',
  })
  @IsNumber()
  @Expose()
  estimatedHours!: number;

  @ApiProperty({
    title: 'Is Approved Hours?',
    type: 'boolean',
  })
  @IsBoolean()
  @Expose()
  isApprovedHours!: boolean;

  @ApiProperty({
    type: 'enum',
    enum: JobPhaseBumpApprovalStatus,
    nullable: true,
    title: 'Spray Ready Hour',
    description: 'The time when the contractor can start.',
  })
  @IsEnum(JobPhaseBumpApprovalStatus)
  @Expose()
  bumperApprovalStatus!: JobPhaseBumpApprovalStatus;

  @ApiProperty({
    title: 'Time In',
    type: 'string',
    example: '08:00:00',
  })
  @IsNotEmpty()
  @IsString()
  @Matches(/^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/, {
    message: 'timeIn must be in HH:MM:SS format',
  })
  @Expose()
  timeIn!: string;

  @ApiProperty({
    title: 'Time Out',
    type: 'string',
    example: '22:00:00',
  })
  @IsNotEmpty()
  @IsString()
  @Matches(/^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/, {
    message: 'timeOut must be in HH:MM:SS format',
  })
  @Expose()
  timeOut!: string;

  @ApiProperty({
    title: 'Date',
    type: 'string',
    format: 'date-time',
  })
  @IsISO8601({ strict: true })
  @Expose()
  date!: Date;

  @ApiProperty({
    title: 'Logged Timestamp',
    type: 'string',
    format: 'date-time',
  })
  @IsISO8601({ strict: true })
  @Expose()
  loggedTimestamp!: Date;

  @ApiProperty({
    title: 'Approved Timestamp',
    type: 'string',
    format: 'date-time',
  })
  @IsISO8601({ strict: true })
  @Expose()
  approvedTimestamp!: Date;

  @ApiProperty({
    title: 'Paid',
    type: 'boolean',
  })
  @IsBoolean()
  @Expose()
  paid!: boolean;

  @ApiProperty({
    type: 'string',
    nullable: true,
    format: 'date-time',
    title: 'Paid Date',
    description: 'Paid Date of this payment',
  })
  @IsISO8601({ strict: true })
  @Expose()
  paidDate!: Date | null;

  @ApiPropertyOptional({
    type: () => GeographyPointDto,
    nullable: true,
    default: null,
    title: 'Logged Geo',
    description: 'GPS location when the bump/payment was logged.',
  })
  @IsOptional()
  @ValidateNested()
  @Type(() => GeographyPointDto)
  @Expose()
  loggedGeo!: GeographyPointDto | null;

  @ApiPropertyOptional({
    type: () => GeographyPointDto,
    nullable: true,
    default: null,
    title: 'Approved Geo',
    description: 'GPS location when the bump/payment was approved.',
  })
  @IsOptional()
  @ValidateNested()
  @Type(() => GeographyPointDto)
  @Expose()
  approvedGeo!: GeographyPointDto | null;

  @ApiPropertyOptional({
    title: 'Logged Geo Accuracy',
    description: 'Accuracy in meters of the GPS reading when logged.',
    nullable: true,
  })
  @IsOptional()
  @IsNumber()
  @Min(0)
  @Expose()
  loggedGeoAccuracy!: number | null;

  @ApiPropertyOptional({
    title: 'Approved Geo Accuracy',
    description: 'Accuracy in meters of the GPS reading when approved.',
    nullable: true,
  })
  @IsOptional()
  @IsNumber()
  @Min(0)
  @Expose()
  approvedGeoAccuracy!: number | null;

  @ApiPropertyOptional({
    enum: GeoMatchStatusEnum,
    nullable: true,
    title: 'Logged Geo Status',
    description: 'Geo match status for the logged event.',
  })
  @IsOptional()
  @IsEnum(GeoMatchStatusEnum)
  @Expose()
  loggedGeoStatus!: GeoMatchStatusEnum | null;

  @ApiPropertyOptional({
    enum: GeoMatchStatusEnum,
    nullable: true,
    title: 'Approved Geo Status',
    description: 'Geo match status for the approved event.',
  })
  @IsOptional()
  @IsEnum(GeoMatchStatusEnum)
  @Expose()
  approvedGeoStatus!: GeoMatchStatusEnum | null;

  @ApiPropertyOptional({
    title: 'Logged Geo Calculated Distance',
    description:
      'Pre-calculated distance in meters between the logged geo and the job geo.',
    nullable: true,
  })
  @IsOptional()
  @IsNumber()
  @Min(0)
  @Expose()
  loggedGeoCalculatedDistance!: number | null;

  @ApiPropertyOptional({
    title: 'Approved Geo Calculated Distance',
    description:
      'Pre-calculated distance in meters between the approved geo and the job geo.',
    nullable: true,
  })
  @IsOptional()
  @IsNumber()
  @Min(0)
  @Expose()
  approvedGeoCalculatedDistance!: number | null;

  @ApiPropertyOptional({
    enum: GeoMatchStatusEnum,
    nullable: true,
    title: 'Geo Match Status',
    description: 'Consolidated worst-case geo match status across all events.',
  })
  @IsOptional()
  @IsEnum(GeoMatchStatusEnum)
  @Expose()
  geoMatchStatus!: GeoMatchStatusEnum | null;

  @ApiPropertyOptional({
    type: 'string',
    format: 'uuid',
    nullable: true,
    title: 'Logged By User ID',
    description: 'ID of the user who logged the bump/payment.',
  })
  @IsOptional()
  @IsUUID()
  @Expose()
  loggedByUserId!: string | null;

  @ApiPropertyOptional({
    type: 'string',
    format: 'uuid',
    nullable: true,
    title: 'Approved By User ID',
    description: 'ID of the user who approved the bump/payment.',
  })
  @IsOptional()
  @IsUUID()
  @Expose()
  approvedByUserId!: string | null;
}
